import React, { useRef, useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  Platform,
  TextInput,
  KeyboardAvoidingView
} from "react-native";
import { Chat, defaultTheme } from "@flyerhq/react-native-chat-ui";
import tinycolor from "tinycolor2";
import {
  FontAwesome,
  MaterialIcons,
  MaterialCommunityIcons
} from "@expo/vector-icons";
import * as Haptics from "expo-haptics";
import Moment from "moment";
import Hyperlink from "react-native-hyperlink";
import getVideoId from "get-video-id";
import Glob from "src/globalConstants";
import Rex from "src/globalState";
import Style from "src/globalStyles";
import Database from "src/backend/database";
import Firebase from "src/backend/firebase";
import Analytics from "src/backend/analytics";
import NavBar from "src/components/navBar";
import Button from "src/components/Button";
import Util from "src/utility";
import Video from "src/components/Video";
import { Skeleton } from "native-base";
import NavBarButton from "src/components/NavBarButton";

const ICON_SIZE = Glob.deviceHasTabletDimensions() ? 42 : 26;

const { height, width } = Glob.get("dimensions");

// For the testing purposes, you should probably use https://github.com/uuidjs/uuid
const uuidv4 = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r % 4) + 8;
    return v.toString(16);
  });
};

const InvoiceSection = ({ invoice, isLoading, onPress, onRefresh }) => {
  if (isLoading) {
    return (
      <View style={styles.invoiceSection}>
        <View style={styles.invoiceHeader}>
          <Skeleton size="10" rounded="full" style={{ marginRight: 12 }} />
          <View style={{ flex: 1 }}>
            <Skeleton.Text lines={2} />
          </View>
        </View>
        <View style={styles.invoiceDetails}>
          <Skeleton.Text lines={3} />
        </View>
      </View>
    );
  }

  if (!invoice) return null;

  const paymentIntent = invoice.payment_intent;
  const paymentStatus = paymentIntent?.status;
  const isPaid = invoice.status === "paid";
  const amount = isPaid ? invoice.amount_paid : invoice.amount_due;
  const dueDate = invoice.due_date
    ? Moment(invoice.due_date * 1000).format("MMM D, YYYY")
    : null;
  const paymentDate = invoice.status_transitions?.paid_at
    ? Moment(invoice.status_transitions.paid_at * 1000).format("MMM D, YYYY")
    : null;

  // Determine status icon and color based on payment state
  let statusIcon = "clock-outline";
  let statusColor = "#F6AD55";
  let statusText = "Invoice Due";

  if (isPaid) {
    statusIcon = "check-circle";
    statusColor = "#2DD881";
    statusText = "Invoice Paid";
  } else if (paymentStatus === "requires_confirmation") {
    statusIcon = "clock-outline";
    statusColor = "#F6AD55";
    statusText = "Unpaid Invoice";
  } else if (paymentStatus === "requires_action") {
    statusIcon = "alert-circle";
    statusColor = "#F6AD55";
    statusText = "Action Required";
  } else if (paymentStatus === "processing") {
    statusIcon = "sync";
    statusColor = "#4299E1";
    statusText = "Processing Payment";
  }

  const showRefreshButton =
    paymentStatus === "processing" || paymentStatus === "requires_confirmation";

  return (
    <TouchableOpacity
      style={styles.invoiceSection}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.invoiceHeader}>
        <MaterialCommunityIcons
          name={statusIcon}
          size={24}
          color={statusColor}
        />
        <View style={styles.invoiceTitleContainer}>
          <Text style={styles.invoiceTitle}>{statusText}</Text>
          <Text style={styles.invoiceAmount}>${(amount / 100).toFixed(2)}</Text>
        </View>
        {showRefreshButton && (
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              onRefresh();
            }}
            style={styles.refreshButton}
          >
            <MaterialIcons name="refresh" size={20} color="#A0AEC0" />
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.invoiceDetails}>
        <View style={styles.invoiceDetailsContent}>
          <Text style={styles.invoiceDetail}>
            {invoice.collection_method === "charge_automatically"
              ? "Payment will be processed automatically"
              : `${isPaid ? "Paid on" : "Due on"} ${
                  isPaid ? paymentDate : dueDate
                }`}
          </Text>
          <Text style={styles.invoiceDetail}>Invoice #{invoice.number}</Text>
          {paymentStatus === "requires_action" && (
            <Text style={[styles.invoiceDetail, { color: statusColor }]}>
              Additional action needed to complete payment
            </Text>
          )}
        </View>
        <MaterialIcons name="chevron-right" size={24} color="#A0AEC0" />
      </View>
    </TouchableOpacity>
  );
};

export default function NotificationDetails({ route, navigation }) {
  const webChatInputRef = useRef(null);
  const { notification, chatRecipient } = route?.params || {};
  const { stripeInvoiceID } = notification || {};
  const [details, setDetails] = useState(null);
  const [chatID, setChatID] = useState(notification?.chat);
  const [showingThread, setShowingThread] = useState(false);
  const [chatMembers, setChatMembers] = useState(null);
  const [otherChatMember, setOtherChatMember] = useState(null);
  const [chatUserNotificationKey, setChatUserNotificationKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(null);
  const [
    notificationContainerIsExpanded,
    setNotificationContainerIsExpanded
  ] = useState(false);
  const [portal, setPortal] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);

  const userID = Firebase.getUserID();
  const chatUser = { id: userID };
  const adminPrivileges = Rex.getSessionMemory("adminPrivileges");
  const allowedToManageUsers =
    !!adminPrivileges && adminPrivileges?.includes("ManageUsers");

  let messageColor = Rex.getConfig()?.colors?.primary || "#565666";
  if (tinycolor(messageColor).isLight())
    messageColor = Rex.getConfig()?.colors?.button || "#565666";
  if (tinycolor(messageColor).isLight()) messageColor = "#565666";

  const possibleURLs = details
    ? Util.detectUrls(`${details.title} ${details.body} ${details.url}`)
    : [];
  const videoLinks = possibleURLs
    .filter((url) => {
      if (!url) return false;
      const service = getVideoId(url)?.service;
      return service === "youtube" || service === "vimeo";
    })
    .slice(0, 3);

  const navigateToPortal = () => {
    if (portal) {
      if (portal.portalType === "native") {
        // e.g. "food"
        navigation.push(portal, {
          navName: portal.portalID,
          txtName: portal.txtName,
          icon: portal.icon,
          portalType: portal.portalType
        });
      } else {
        // e.g. "webNav"
        navigation.push(portal.portalType, {
          navName: portal.portalID,
          txtName: portal.txtName,
          icon: portal.icon,
          portalType: portal.portalType
        });
      }
    }
  };

  useEffect(() => {
    if (notification?.key) {
      Database.markUserNotificationAsOpened(notification.key);
    }
    // If this has a chat, then fetch the original notification this chat is based on
    if (chatID) {
      Database.subscribeToChat(chatID, onChatChange);
      Analytics.logEvent("view_chat", { chatID });
    } else if (notification) {
      Database.fetchNotificationDetails(
        notification.notificationDetails || notification.key
      ).then((notificationDetails) => {
        if (notificationDetails) {
          const formattedNotificationForAnalytics = {
            ...notificationDetails,
            audience: JSON.stringify(notificationDetails.audience)
          };
          Analytics.logEvent(
            "view_notificationDetails",
            formattedNotificationForAnalytics
          );
          setDetails(notificationDetails);
        }
        // If there are no notification details, then this is a personalized notification
        else {
          setDetails(notification);
        }
      });
      // If this notification references an invoice, then fetch the invoice
      if (notification?.stripeInvoiceID) {
        setIsLoadingCustomer(true);
        loadInvoice();
        Database.stripeConnectedAccountFetchCustomer().then((response) => {
          const { success } = response || {};
          if (success !== false) {
            setCustomer(response);
          }
          setIsLoadingCustomer(false);
        });
      }
    }
    // If we're trying to create a message to someone
    else if (chatRecipient) {
      // First check if we already have a chat with them
      const self = userID;
      const other = chatRecipient.uid;
      const chatKey = Database.membersToDirectChatID(self, other);
      Database.checkIfChatExistsByID(chatKey).then((exists) => {
        if (exists) {
          Analytics.logEvent("view_chat", { chatID: chatKey });
          Database.subscribeToChat(chatKey, onChatChange);
          setChatID(chatKey);
        }
        Analytics.logEvent("view_chat", { newChat: true });
        setShowingThread(true);
      });
    } else {
      Analytics.logEvent("view_chat", { newChat: true });
      setShowingThread(true);
    }

    // cleanup called when component is unmounting
    return () => {
      if (chatID) Database.unsubscribeFromChat(chatID);
      if (notification?.key)
        Database.markUserNotificationAsOpened(notification.key);
    };
  }, []);

  useEffect(() => {
    if (allowedToManageUsers && chatMembers) {
      const otherMembers = chatMembers.filter((m) => m !== userID);
      if (otherMembers?.length === 1) {
        const otherUserID = otherMembers[0];
        Database.fetchUserByID(otherUserID).then((otherUser) => {
          setOtherChatMember({ ...otherUser, uid: otherUserID });
        });
      }
    }
  }, [chatMembers]);

  useEffect(() => {
    if (details?.portalID) {
      Database.getPortalMetadata(details.portalID).then((metadata) => {
        if (metadata) setPortal({ ...metadata, portalID: details.portalID });
      });
    }
  }, [details]);

  const loadInvoice = () => {
    setIsLoadingInvoice(true);
    Database.stripeConnectedAccountFetchInvoice(
      notification.stripeInvoiceID
    ).then((response) => {
      const { success, invoice: invoiceData } = response || {};
      if (success) {
        setInvoice(invoiceData);
      }
      setIsLoadingInvoice(false);
    });
  };

  const onChatChange = (fetchedChat) => {
    setChatMembers(Object.keys(fetchedChat?.members));
    setChatUserNotificationKey(fetchedChat?.userNotificationKey);
    if (Platform.OS !== "web")
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (fetchedChat.thread) {
      const { thread } = fetchedChat;
      setShowingThread(true);
      const currentThread = Object.keys(thread)
        .sort((a1, a2) => {
          if (a1 < a2) return 1;
          if (a1 > a2) return -1;
          return 0;
        })
        .map((k) => ({
          author: { id: thread[k].sender },
          createdAt: new Date(parseInt(k, 10)),
          id: uuidv4(),
          text: thread[k].message,
          type: "text"
        }));
      setMessages(currentThread);
    }
    if (fetchedChat.notification) {
      Database.fetchNotificationDetails(fetchedChat.notification).then(
        (notificationDetails) => {
          const formattedNotificationForAnalytics = {
            ...notificationDetails,
            audience: JSON.stringify(notificationDetails.audience)
          };
          Analytics.logEvent(
            "view_notificationDetails",
            formattedNotificationForAnalytics
          );
          setDetails(notificationDetails);
        }
      );
    }
  };

  const handleChatPreviewDataFetched = ({ message, previewData }) => {
    setMessages(
      messages.map((m) => (m.id === message.id ? { ...m, previewData } : m))
    );
  };

  const addMessage = (message) => {
    setMessages([message, ...messages]);
  };

  const onSendChatMessage = (message) => {
    const textMessage = {
      author: chatUser,
      createdAt: Date.now(),
      id: uuidv4(),
      text: message.text,
      type: "text"
    };
    let chatTitle = notification?.title || null;
    if (chatRecipient)
      chatTitle = `${chatRecipient.firstName} ${chatRecipient.lastName}`;
    if (chatID) {
      Analytics.logEvent("touch_chat_sendMessage", { chatID });
      if (notification?.key) {
        Database.replyToChatThread(
          chatID,
          message.text,
          notification.key,
          chatTitle
        ).then(() => {});
      } else if (chatUserNotificationKey) {
        Database.replyToChatThread(
          chatID,
          message.text,
          chatUserNotificationKey,
          chatTitle
        ).then(() => {});
      } else {
        Database.fetchUserNotificationByChat(chatID).then((notificationKey) => {
          if (notificationKey) {
            Database.replyToChatThread(
              chatID,
              message.text,
              notificationKey,
              chatTitle
            ).then(() => {});
          } else {
            console.log("error");
          }
        });
      }
    } else {
      Analytics.logEvent("touch_chat_sendMessage", { newChat: true });
      Database.createNewChat(
        [userID, details?.sender || chatRecipient?.uid],
        message.text,
        notification,
        chatTitle,
        true
      ).then((createdChatID) => {
        Analytics.logEvent("touch_chat_createdNewChat", {
          chatID: createdChatID
        });
        setChatID(createdChatID);
        Database.subscribeToChat(createdChatID, onChatChange);
      });
    }
    addMessage(textMessage);
  };

  const navigateToLink = (title, url) => {
    Analytics.logEvent("touch_notificationDetails_openedLink", { title, url });
    // One school had an issue where a Signup Genius page didn't format well within the app
    // Specifically, this URL: https://www.signupgenius.com/tabs/5317FD801A1CFECCF9-allschool
    if (url.includes("signupgenius.com")) Util.openURL(url);
    else navigation.push("webNav", { title, url });
  };

  const renderChatMessageBubble = ({ child, message, nextMessageInGroup }) => {
    const [showTime, setShowTime] = useState(false);
    const userIsAuthor = userID === message.author.id;
    return (
      <>
        {showTime && (
          <Text
            style={{
              marginTop: 10,
              color: "gray",
              textAlign: userIsAuthor ? "right" : "left"
            }}
          >
            Sent at {Moment(message.createdAt).format("h:mm:ss A")}
          </Text>
        )}
        <TouchableOpacity
          onPress={() => setShowTime(!showTime)}
          style={{
            backgroundColor: userIsAuthor ? messageColor : "#f5f5f7",
            borderRadius: 20,
            borderBottomLeftRadius:
              !nextMessageInGroup && !userIsAuthor ? 0 : 20,
            borderBottomRightRadius:
              !nextMessageInGroup && userIsAuthor ? 0 : 20,
            overflow: "hidden"
          }}
        >
          {child}
        </TouchableOpacity>
      </>
    );
  };

  let navBarTitle = notification?.title || "Message";
  if (chatRecipient)
    navBarTitle = `${chatRecipient.firstName} ${chatRecipient.lastName}`;
  else if (!notification?.title && otherChatMember) {
    // If there's no title, but user is admin, we can set the title to be the other user's name
    // An example where there might be no title is if user had deleted the thread, then the person chatted again.
    navBarTitle = `${otherChatMember.firstName} ${otherChatMember.lastName}`;
  }

  const chatIsWithSelf =
    (chatMembers?.length === 1 && chatMembers[0] === userID) ||
    chatRecipient?.uid === userID ||
    (!chatMembers && details?.sender === userID);

  let RightButton = null;
  if (allowedToManageUsers && otherChatMember)
    RightButton = (
      <NavBarButton
        data={{ icon: "1b0091cf-243d-4a1e-be23-9ed2ac19169b" }} // person
        onPress={() =>
          navigation.push("manageUser", {
            user: otherChatMember,
            isFromChat: true
          })
        }
      />
    );

  return (
    <KeyboardAvoidingView
      style={styles.pageContent}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <NavBar
        navigation={navigation}
        text={navBarTitle}
        RightButton={RightButton}
      />
      {!Rex.getLoginStatus() ? (
        <View style={{ width, alignSelf: "center", padding: 20 }}>
          <Text style={Style.get("headerText")}>You're not signed in.</Text>
          <Text style={Style.get("subheaderText")}>
            To use this feature, please go back and log into this app (the
            button in the top left corner).
          </Text>
        </View>
      ) : (
        <>
          {!details && !showingThread && (
            <ActivityIndicator size="large" style={{ marginTop: 50 }} />
          )}
        </>
      )}
      {!!details && (
        <View
          style={[
            styles.notificationContainer,
            notificationContainerIsExpanded ? { flex: 6 } : {}
          ]}
        >
          <ScrollView
            persistentScrollbar={notificationContainerIsExpanded}
            style={{ width, padding: 20, paddingBottom: 3 }}
            scrollIndicatorInsets={{ right: 1 }}
          >
            <Text style={styles.notificationDate}>
              Sent {Util.friendlyDate(details.timestamp, { isLong: false })} at{" "}
              {Moment(details.timestamp).format("h:mma")}
            </Text>
            <Text style={styles.notificationTitle}>{details.title}</Text>
            <Hyperlink
              linkStyle={{
                color: Rex.getConfig()?.colors?.button,
                textDecorationLine: "underline"
              }}
              onPress={(url) => {
                if (Platform.OS === "web" || url.includes("mailto:"))
                  Util.openURL(url);
                else if (navigation)
                  navigation.push("webNav", {
                    title: details.title,
                    url
                  });
              }}
            >
              <Text style={styles.notificationBody}>{details.body}</Text>
            </Hyperlink>
            {!!portal && (
              <Button text={`${portal.txtName}`} onPress={navigateToPortal} />
            )}
            {!!details.url && (
              <Button
                text="Open Link"
                onPress={() => navigateToLink(details.title, details.url)}
              />
            )}
            {!!stripeInvoiceID && (
              <InvoiceSection
                invoice={invoice}
                isLoading={isLoadingInvoice || isLoadingCustomer}
                onPress={() => {
                  navigation.push("paymentsCustomerInvoiceDetails", {
                    invoice,
                    customerProfile: customer,
                    onPaymentSuccess: loadInvoice,
                    isPaidInvoice: invoice?.status === "paid"
                  });
                  // If the invoice is processing, make sure to reload so it shows the same status as the viewable invoice when the navigate back
                  setTimeout(loadInvoice, 500);
                }}
                onRefresh={loadInvoice}
              />
            )}
            {!showingThread &&
              !!Rex.getConfig()?.chatEnabled &&
              !!details?.chatEnabled && (
                <Button text="Reply" onPress={() => setShowingThread(true)} />
              )}
            {videoLinks.length > 0 && (
              <View>
                {videoLinks.map((url) => (
                  <Video key={url} source={{ uri: url }} />
                ))}
              </View>
            )}
          </ScrollView>
          {showingThread && (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.notificationContainerExpandButton}
              onPress={() => {
                setNotificationContainerIsExpanded(
                  !notificationContainerIsExpanded
                );
              }}
            >
              <MaterialIcons
                name={`expand-${
                  notificationContainerIsExpanded ? "less" : "more"
                }`}
                size={24}
                color="black"
              />
            </TouchableOpacity>
          )}
        </View>
      )}
      {showingThread && (
        <View style={{ flex: 4.5, marginTop: 10, alignItems: "center" }}>
          <View
            style={{
              width,
              height: "100%",
              borderColor: "#eee",
              borderLeftWidth: 1,
              borderRightWidth: 1
            }}
          >
            <Chat
              sendButtonVisibilityMode="always"
              onPreviewDataFetched={handleChatPreviewDataFetched}
              timeFormat="h:mm A"
              theme={{
                ...defaultTheme,
                colors: {
                  ...defaultTheme.colors,
                  inputBackground: "#404040",
                  primary: messageColor
                }
              }}
              renderBubble={renderChatMessageBubble}
              textInputProps={{
                placeholder: chatIsWithSelf ? "Note to self" : "Message"
              }}
              messages={messages}
              onSendPress={onSendChatMessage}
              user={chatUser}
              customBottomComponent={() => {
                return (
                  <View style={styles.chatInputContainer}>
                    <TextInput
                      ref={webChatInputRef}
                      placeholder={chatIsWithSelf ? "Note to self" : "Message"}
                      style={[
                        styles.chatInput,
                        { marginRight: newMessage ? 0 : 20 }
                      ]}
                      value={newMessage}
                      onChangeText={setNewMessage}
                      selectionColor={messageColor}
                      multiline
                      numberOfLines={2}
                    />
                    {newMessage ? (
                      <TouchableOpacity
                        style={styles.chatSendButton}
                        activeOpacity={0.6}
                        onPress={() => {
                          onSendChatMessage({ text: newMessage });
                          setNewMessage(null);
                          webChatInputRef.current.clear();
                        }}
                      >
                        <FontAwesome
                          name="send"
                          size={ICON_SIZE}
                          color={Rex.getConfig()?.colors?.button}
                        />
                      </TouchableOpacity>
                    ) : null}
                  </View>
                );
              }}
            />
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const notificationContainer = {
  flex: 1,
  alignItems: "center",
  backgroundColor: "white",
  shadowOpacity: 0.3,
  shadowRadius: 5,
  shadowOffset: { width: 0, height: 0 },
  elevation: 7
};
if (Platform.OS === "web")
  notificationContainer.boxShadow = "0px 0px 5px rgba(0,0,0,0.3)";
const styles = StyleSheet.create({
  pageContent: {
    backgroundColor: "white",
    height
  },
  notificationContainer,
  notificationTitle: {
    fontSize: 20,
    marginVertical: 10
  },
  notificationContainerExpandButton: {
    justifyContent: "center",
    alignItems: "center",
    opacity: 0.8,
    position: "absolute",
    bottom: 0,
    width: "100%",
    backgroundColor: "white",
    alignSelf: "center"
  },
  notificationDate: {
    fontSize: 12,
    color: "gray"
  },
  notificationBody: {
    fontSize: 15,
    marginBottom: 20
  },
  chatInputContainer: {
    width: "100%",
    flexDirection: "row"
  },
  chatInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: "gray",
    borderRadius: 10,
    margin: 20,
    padding: 10,
    minHeight: 40,
    fontSize: 16
  },
  chatSendButton: {
    alignItems: "center",
    justifyContent: "center",
    padding: 20
  },
  invoiceSection: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    ...(Platform.OS === "web" && {
      cursor: "pointer",
      transition: "all 0.2s ease",
      ":hover": {
        transform: "translateY(-1px)",
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 4
      }
    })
  },
  invoiceHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12
  },
  invoiceTitleContainer: {
    flex: 1,
    marginLeft: 12
  },
  invoiceTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 4
  },
  invoiceAmount: {
    fontSize: 20,
    fontWeight: "700",
    color: "#2D3748"
  },
  invoiceDetails: {
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
    paddingTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  invoiceDetailsContent: {
    flex: 1
  },
  invoiceDetail: {
    fontSize: 14,
    color: "#718096",
    marginBottom: 4
  },
  refreshButton: {
    paddingVertical: 12,
    paddingRight: 4,
    paddingLeft: 30,
    marginLeft: 8
  }
});
